export interface WalletLike {
	connected?: boolean;
	name?: string;
	signTransaction?: (...args: unknown[]) => unknown;
	signAllTransactions?: (...args: unknown[]) => unknown;
}

interface FluxBotRestoreInput {
	hasFluxBotAuth: boolean;
	currentPath: unknown;
	redirectPath?: unknown;
}

interface WalletRouteInput {
	currentPath: unknown;
	wallet: WalletLike | null | undefined;
}

interface WalletLoginGuardInput {
	loginPath: unknown;
	redirectPath?: unknown;
	isConnected: boolean;
	isBotWallet: boolean;
}

function normalizeRoutePath(path: unknown): string {
	return String(path || '').split('?')[0];
}

function walletDisplayName(wallet: WalletLike | null | undefined): string {
	return wallet?.name || 'Connected wallet';
}

function unsupportedSignerError(wallet: WalletLike | null | undefined): Error {
	return new Error(
		`${walletDisplayName(
			wallet
		)} cannot sign this transaction in the web app. Connect a browser wallet that owns the required assets and try again.`
	);
}

export function routeRequiresBrowserSigner(path: unknown): boolean {
	const routePath = normalizeRoutePath(path);
	if (routePath.startsWith('/app/tools')) return true;
	if (routePath === '/app/liquidity') return true;
	return routePath.startsWith('/app/pools/');
}

export function canSignTransaction(wallet: WalletLike | null | undefined): boolean {
	return typeof wallet?.signTransaction === 'function';
}

export function canSignAllTransactions(wallet: WalletLike | null | undefined): boolean {
	return typeof wallet?.signAllTransactions === 'function';
}

export function assertCanSignTransaction(wallet: WalletLike | null | undefined): void {
	if (canSignTransaction(wallet)) return;
	throw unsupportedSignerError(wallet);
}

export function assertCanSignAllTransactions(wallet: WalletLike | null | undefined): void {
	if (canSignAllTransactions(wallet)) return;
	throw unsupportedSignerError(wallet);
}

export function shouldRestoreFluxBotWallet({
	hasFluxBotAuth,
	currentPath,
	redirectPath,
}: FluxBotRestoreInput): boolean {
	if (!hasFluxBotAuth) return false;
	if (routeRequiresBrowserSigner(currentPath)) return false;
	return !routeRequiresBrowserSigner(redirectPath);
}

export function shouldRedirectToWalletLogin({ currentPath, wallet }: WalletRouteInput): boolean {
	if (!wallet?.connected) return true;
	if (!routeRequiresBrowserSigner(currentPath)) return false;
	return !canSignTransaction(wallet);
}

export function shouldRedirectConnectedWalletFromLogin({
	loginPath,
	redirectPath,
	isConnected,
	isBotWallet,
}: WalletLoginGuardInput): boolean {
	if (normalizeRoutePath(loginPath) !== '/auth/login') return false;
	if (!isConnected) return false;
	if (!isBotWallet) return true;
	return !routeRequiresBrowserSigner(redirectPath);
}

export const getFluxBotToken = (walletAddress: string): string => {
	const storedAuth = localStorage.getItem('fluxbot:auth');
	const walletName = localStorage.getItem('walletName');

	if (!storedAuth || walletName !== 'FluxBot') return '';

	const session = JSON.parse(storedAuth);
	if (session?.wallet !== walletAddress) return '';

	return `Bearer ${session?.token}`;
};
